import * as B from '@blueprintjs/core';
import React from 'react';

import {ApiFeature} from 'app/modules/Remote/Feature/types';
import {alphanumericSort, formatFeatureName} from 'app/utils/featureUtils';

import cs from './styles.module.scss';

export const PropertyPicker: React.FC<{
  features: ApiFeature[];
  selected: ApiFeature[];
  onChange: (selection: ApiFeature[]) => void;
}> = ({features, selected, onChange}) => {
  const [query, setQuery] = React.useState('');

  const rows = React.useMemo(
    () =>
      features
        .map((feature) => ({feature, name: formatFeatureName(feature)}))
        .sort((a, b) => alphanumericSort(a.name, b.name)),
    [features]
  );

  const visibleRows = React.useMemo(() => {
    const search = query.trim().toLowerCase();
    return search ? rows.filter((r) => r.name.toLowerCase().includes(search)) : rows;
  }, [rows, query]);

  const selectedIds = React.useMemo(
    () => new Set(selected.map((f) => f.properties.id)),
    [selected]
  );

  const allVisibleSelected =
    visibleRows.length > 0 && visibleRows.every((r) => selectedIds.has(r.feature.properties.id));

  // The selection can span portfolios; the count here is only for the one on screen.
  const selectedHereCount = rows.filter((r) => selectedIds.has(r.feature.properties.id)).length;

  const toggle = (feature: ApiFeature) =>
    onChange(
      selectedIds.has(feature.properties.id)
        ? selected.filter((f) => f.properties.id !== feature.properties.id)
        : [...selected, feature]
    );

  // Acts on what's on screen, so a search narrows what a bulk toggle touches.
  const toggleVisible = () => {
    if (allVisibleSelected) {
      const visibleIds = new Set(visibleRows.map((r) => r.feature.properties.id));
      onChange(selected.filter((f) => !visibleIds.has(f.properties.id)));
    } else {
      const added = visibleRows
        .filter((r) => !selectedIds.has(r.feature.properties.id))
        .map((r) => r.feature);
      onChange([...selected, ...added]);
    }
  };

  return (
    <div className={cs.picker}>
      <div className={cs.pickerToolbar}>
        <B.InputGroup
          className={cs.pickerSearch}
          leftIcon="search"
          placeholder="Search properties..."
          value={query}
          onValueChange={setQuery}
        />
        <B.Button
          minimal
          disabled={visibleRows.length === 0}
          text={allVisibleSelected ? 'Clear all' : 'Select all'}
          onClick={toggleVisible}
        />
      </div>
      {visibleRows.length === 0 ? (
        <div className={cs.pickerEmpty}>
          {features.length === 0 ? 'This portfolio has no properties.' : 'No properties match.'}
        </div>
      ) : (
        <B.CardList bordered compact className={cs.propertyList}>
          {visibleRows.map(({feature, name}) => (
            <B.CheckboxCard
              key={feature.properties.id}
              className={cs.propertyRow}
              alignIndicator="left"
              checked={selectedIds.has(feature.properties.id)}
              onChange={() => toggle(feature)}
            >
              <span className={cs.propertyName}>{name}</span>
            </B.CheckboxCard>
          ))}
        </B.CardList>
      )}
      <div className={cs.selectionCount}>
        {selectedHereCount} of {features.length} selected
      </div>
    </div>
  );
};
